import { Extension } from "@tiptap/core";
import { Plugin, TextSelection } from "@tiptap/pm/state";

import { WRAPPER_NODE_CLASS_NAME } from "@/models/CursorModel";
import { keyMatcher } from "@/utils/keyMatcher";
import { trackEvent } from "@/utils/trackEvent";

import { closestCollapsedBlock, collapsedRunSelection } from "../collapse/blockCollapse";
import { isBlockCollapsible, isCollapsed } from "../collapse/utils";
import { getWordRangeAt } from "../utils/getWordRangeAt";

import { openSelectionSuggestions, setInputValueFromSelection } from "./utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionContext } from "@/models/SuggestionsModel";
import type { EditorView } from "@tiptap/pm/view";

/**
 * Single owner of opening the right-click (context) menu. It listens to the
 * right-click, makes sure the right selection is in place, and opens the
 * suggestions popup anchored at the cursor.
 */
export const ContextMenu = Extension.create<{ doc: DocumentModel }>({
  name: "contextMenu",

  onTransaction({ transaction }) {
    const { doc } = this.options;
    if (transaction.docChanged && doc.editor.suggestions.pointerAnchor) {
      doc.editor.suggestions.reset();
    }
  },

  addProseMirrorPlugins() {
    const { doc } = this.options;

    const openForMathSelection = (x: number, y: number): SuggestionContext | null => {
      if (!doc.selection.isMathOnlySelection) {
        return null;
      }
      doc.editor.suggestions.openAt(x, y, "math_node");
      return "math_node";
    };

    const openForMathNode = (
      target: HTMLElement,
      x: number,
      y: number,
    ): SuggestionContext | null => {
      const nodeId = target.closest<HTMLElement>("[data-math-node-id]")?.dataset.mathNodeId;
      const node = nodeId ? doc.nodes.get(nodeId) : undefined;
      if (!node) {
        return null;
      }

      doc.editor.suggestions.openAt(x, y, "math_node", node);
      return "math_node";
    };

    const openForCollapsedBlock = (
      view: EditorView,
      target: HTMLElement,
      x: number,
      y: number,
    ): SuggestionContext | null => {
      const wrapper = target.closest(".line-wrapper.is-collapsed");
      if (!wrapper) {
        return null;
      }

      const $pos = view.state.doc.resolve(view.posAtDOM(wrapper, 0));
      const after = $pos.nodeAfter;

      let found = closestCollapsedBlock($pos);

      if (!found) {
        found =
          after && isCollapsed(after) && isBlockCollapsible(after)
            ? { node: after, pos: $pos.pos }
            : null;
      }

      if (!found) {
        return null;
      }

      view.dispatch(view.state.tr.setSelection(collapsedRunSelection(view, found.pos)));
      doc.editor.suggestions.openAt(x, y, "collapsed_block");
      return "collapsed_block";
    };

    const openForSelection = (view: EditorView, x: number, y: number): SuggestionContext | null => {
      const { selection } = view.state;
      if (selection.empty) {
        return null;
      }

      const coords = view.posAtCoords({ left: x, top: y });
      if (!coords || coords.pos < selection.from || coords.pos > selection.to) {
        return null;
      }

      return openSelectionSuggestions(doc, view, x, y);
    };

    const openForWord = (view: EditorView, x: number, y: number): SuggestionContext | null => {
      const coords = view.posAtCoords({ left: x, top: y });
      if (!coords) {
        return null;
      }

      const { selection } = view.state;
      if (selection.empty && selection.from === coords.pos) {
        return null;
      }

      const wordRange = getWordRangeAt(view.state, coords.pos);
      if (!wordRange) {
        return null;
      }
      view.dispatch(
        view.state.tr.setSelection(
          TextSelection.create(view.state.doc, wordRange.from, wordRange.to),
        ),
      );

      const [start, end, value] = setInputValueFromSelection(doc, view);
      if (!value) {
        return null;
      }
      if (!doc.editor.suggestions.available) {
        return null;
      }

      doc.editor.suggestions.openAt(x, y, "text_selection");
      doc.input.setInputPosRange({ from: start, to: end }, true);
      return "text_selection";
    };

    const openForEmptySpace = (
      view: EditorView,
      x: number,
      y: number,
    ): SuggestionContext | null => {
      const coords = view.posAtCoords({ left: x, top: y });
      if (!coords) {
        return null;
      }

      doc.input.reset();
      doc.editor.suggestions.openAt(x, y, "empty_space");
      view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, coords.pos)));
      return "empty_space";
    };

    const openContextMenu = (
      view: EditorView,
      target: HTMLElement,
      x: number,
      y: number,
    ): boolean => {
      const onMath = !!target.closest(`.${WRAPPER_NODE_CLASS_NAME}`);
      const context =
        (onMath ? (openForMathSelection(x, y) ?? openForMathNode(target, x, y)) : null) ??
        openForCollapsedBlock(view, target, x, y) ??
        openForSelection(view, x, y) ??
        openForWord(view, x, y) ??
        openForEmptySpace(view, x, y);

      if (!context) {
        return false;
      }

      trackEvent("suggestions_opened", { context, source: "right_click", doc_id: doc.id });
      return true;
    };

    return [
      new Plugin({
        props: {
          handleKeyDown(_view, e) {
            // The menu's own list/submenu navigation consumes arrows in the capture phase
            // (setting defaultPrevented). An arrow that still reaches here is a real caret
            // move, so dismiss the anchored menu and let it fall through to the editor.
            if (
              doc.editor.suggestions.pointerAnchor &&
              !e.defaultPrevented &&
              (keyMatcher("ArrowLeft", e) || keyMatcher("ArrowRight", e))
            ) {
              doc.editor.suggestions.reset();
            }

            return false;
          },
        },
        view(view) {
          const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 2 || e.shiftKey || !doc.editable) {
              return;
            }

            const target = e.target as HTMLElement | null;
            if (!target || !view.dom.contains(target)) {
              return;
            }

            if (openContextMenu(view, target, e.clientX, e.clientY)) {
              e.preventDefault();
            }
          };

          const onContextMenu = (e: MouseEvent) => {
            if (e.shiftKey || !doc.editable) {
              return;
            }

            const target = e.target as HTMLElement | null;
            if (!target || !view.dom.contains(target)) {
              return;
            }

            if (
              doc.editor.suggestions.pointerAnchor ||
              target.closest(`.${WRAPPER_NODE_CLASS_NAME}`)
            ) {
              e.preventDefault();
            }
          };

          document.addEventListener("mousedown", onMouseDown);
          document.addEventListener("contextmenu", onContextMenu, true);

          return {
            destroy() {
              document.removeEventListener("mousedown", onMouseDown);
              document.removeEventListener("contextmenu", onContextMenu, true);
            },
          };
        },
      }),
    ];
  },
});
